import React from "react";
import LegalLayout from "../components/legal/LegalLayout";
import SEOHead from "../components/SEOHead";
import { Cookie } from "lucide-react";

const COOKIE_TABLE = [
  {
    name: "binshare_session",
    type: "Essential",
    purpose: "Keeps you signed in to the Binshare app.",
    expires: "Session / 30 days",
  },
  {
    name: "csrf_token",
    type: "Essential",
    purpose: "Protects against cross-site request forgery on form submission.",
    expires: "Session",
  },
  {
    name: "binshare_cookie_consent",
    type: "Essential (localStorage)",
    purpose: "Remembers whether you accepted or rejected analytics cookies.",
    expires: "Until you clear it",
  },
  {
    name: "ph_*_posthog",
    type: "Analytics (consent required)",
    purpose:
      "PostHog analytics, hosted in the EU. Counts page views only. Only set if you click Accept. No session recording and no form text is recorded.",
    expires: "Up to 12 months",
  },
];

const SECTIONS = [
  {
    id: "what-are-cookies",
    label: "1. What are cookies?",
    content: (
      <p>
        Cookies are small text files placed on your device when you visit a
        website. They allow the site to recognise your device, remember
        preferences, keep you signed in and understand how the site is used.
        Some technologies we use (e.g. localStorage) work similarly and are
        also covered by this policy.
      </p>
    ),
  },
  {
    id: "categories",
    label: "2. Categories of cookies we use",
    content: (
      <>
        <p>
          We group cookies into two categories. Only essential cookies are set
          by default; analytics cookies are only set with your consent.
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>Essential</strong> — required for the Service to function:
            authentication, security, and remembering your cookie choices. You
            cannot opt out of these.
          </li>
          <li>
            <strong>Analytics</strong> — help us understand how Binshare is
            used so we can improve it. We use PostHog (EU servers) to count
            page views. It only runs after you click Accept on the cookie
            banner. Session recording is off, we do not record what you type
            into forms, and we never share data with advertising networks.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "cookie-list",
    label: "3. Cookies we set",
    content: (
      <div className="overflow-x-auto not-prose">
        <table className="min-w-full border border-moss-200/50 rounded-xl text-sm">
          <thead className="bg-cream-100/60">
            <tr className="text-left">
              <th className="px-4 py-3 font-semibold text-moss-900">Name</th>
              <th className="px-4 py-3 font-semibold text-moss-900">Type</th>
              <th className="px-4 py-3 font-semibold text-moss-900">
                Purpose
              </th>
              <th className="px-4 py-3 font-semibold text-moss-900">
                Expires
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-moss-200/40">
            {COOKIE_TABLE.map((c) => (
              <tr key={c.name} className="align-top">
                <td className="px-4 py-3 font-mono text-xs text-moss-800">
                  {c.name}
                </td>
                <td className="px-4 py-3 text-moss-700 whitespace-nowrap">
                  {c.type}
                </td>
                <td className="px-4 py-3 text-moss-700">{c.purpose}</td>
                <td className="px-4 py-3 text-moss-700 whitespace-nowrap">
                  {c.expires}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ),
  },
  {
    id: "third-party",
    label: "4. Third-party cookies",
    content: (
      <p>
        Where embedded content (for example our payment provider&apos;s
        checkout) is loaded from a third-party domain, that provider may set
        its own cookies under its own privacy and cookie policies. We only
        embed third-party services that are required for the Service to
        function.
      </p>
    ),
  },
  {
    id: "managing",
    label: "5. Managing your choices",
    content: (
      <>
        <p>You can manage cookies in two places:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>On this website</strong> — click &quot;Cookie settings&quot;
            in the footer to reopen the cookie banner and change your
            analytics choice at any time.
          </li>
          <li>
            <strong>In your browser</strong> — every major browser lets you
            block or delete cookies. Be aware that blocking essential cookies
            will prevent you from signing in or using parts of the Service.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "do-not-track",
    label: "6. Do Not Track",
    content: (
      <p>
        We honour the Global Privacy Control (GPC) signal: if your browser
        sends GPC we treat that as a withdrawal of consent for analytics
        cookies.
      </p>
    ),
  },
  {
    id: "changes",
    label: "7. Changes to this policy",
    content: (
      <p>
        If we introduce new cookies, change retention periods or switch
        analytics providers, we will update the table above and the &quot;Last
        updated&quot; date.
      </p>
    ),
  },
  {
    id: "contact",
    label: "8. Contact us",
    content: (
      <p>
        Cookie questions? Email{" "}
        <a
          href="mailto:support@binshare.co.uk"
          className="text-terracotta-700 underline underline-offset-2 font-medium"
        >
          support@binshare.co.uk
        </a>
        . See our{" "}
        <a
          href="/privacy"
          className="text-terracotta-700 underline underline-offset-2"
        >
          Privacy Policy
        </a>{" "}
        for the wider picture.
      </p>
    ),
  },
];

export default function CookiePage() {
  return (
    <>
      <SEOHead
        path="/cookies"
        title="Cookie Policy · Binshare"
        description="Which cookies and similar technologies Binshare uses, what they do, and how to manage them."
      />
      <LegalLayout
        testidPrefix="cookies"
        eyebrow={
          <>
            <Cookie className="w-3.5 h-3.5" /> Legal
          </>
        }
        title="Cookie Policy"
        intro="Which cookies and similar technologies Binshare uses, what they do, and how to manage them."
        effective="15 December 2025"
        lastUpdated="13 October 2026"
        sections={SECTIONS}
      />
    </>
  );
}
